/**
 * Shared layout chrome for every authenticated page.
 *
 * Collapsible left sidebar + main content well. State (expanded vs collapsed)
 * is persisted in localStorage so it doesn't reset across page navigations.
 * Active route gets a highlight pill on its sidebar item. Owner-only links
 * (Clients, Add client) hide automatically when the signed-in user isn't an
 * owner — wires through the `userRole` prop the calling page reads from /api/me.
 */

import { UserButton } from '@clerk/clerk-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

type Role = 'owner' | 'va' | 'va_admin' | 'client' | null;

const STORAGE_KEY = 'gp.sidebar.collapsed';

type NavItem = {
  to: string;
  label: string;
  icon: string;
  /** Hidden unless the user is owner or va_admin (admin-VAs manage clients
   *  too but don't get the Team page since that's billing/membership). */
  adminOnly?: boolean;
  /** Strict owner-only — billing / team membership. */
  ownerOnly?: boolean;
};

// `divider: true` renders a thin rule above the item — used to visually
// group the personal items (Profile, Settings) apart from the workspace
// nav, the way macOS sidebars segment sources.
type NavEntry = NavItem & { divider?: boolean };

const NAV: NavEntry[] = [
  { to: '/dashboard', label: 'Dashboard', icon: '🏠' },
  { to: '/leads', label: 'Leads', icon: '⚡' },
  { to: '/admin/clients', label: 'Clients', icon: '👥', adminOnly: true },
  { to: '/admin/new-client', label: 'Add Client', icon: '➕', ownerOnly: true },
  { to: '/admin/team', label: 'Team', icon: '🤝', ownerOnly: true },
  { to: '/profile', label: 'Profile', icon: '🧭', divider: true },
  { to: '/settings', label: 'Settings', icon: '⚙️' },
];

export function AppShell({
  children,
  userRole,
}: {
  children: ReactNode;
  /** Owner-only items hide when this is not 'owner'. Pass null while loading. */
  userRole?: Role;
}) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  });
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    }
  }, [collapsed]);

  // Admin-only items show for owner + va_admin. Strict ownerOnly items show
  // only for owner. While loading (userRole undefined) we show everything to
  // avoid a flash of items appearing then disappearing once /api/me resolves.
  const items = NAV.filter((n) => {
    if (!n.ownerOnly && !n.adminOnly) return true;
    if (userRole === undefined) return true;
    if (n.ownerOnly) return userRole === 'owner';
    return userRole === 'owner' || userRole === 'va_admin';
  });

  return (
    <div className={`shell ${collapsed ? 'shell-collapsed' : ''}`}>
      <aside className="shell-sidebar">
        <div className="shell-brand">
          <Link to="/dashboard" className="shell-brand-link">
            <span className="shell-brand-dot" />
            {!collapsed && <span className="shell-brand-text">GroupPulse</span>}
          </Link>
        </div>

        <nav className="shell-nav" aria-label="Main">
          {items.map((item) => {
            const isActive =
              pathname === item.to ||
              (item.to !== '/dashboard' && pathname.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`shell-nav-item ${isActive ? 'is-active' : ''} ${
                  item.divider ? 'shell-nav-divider' : ''
                }`}
                title={collapsed ? item.label : undefined}
              >
                <span className="shell-nav-icon" aria-hidden="true">
                  {item.icon}
                </span>
                {!collapsed && (
                  <span className="shell-nav-label">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="shell-sidebar-foot">
          <button
            type="button"
            className="shell-collapse-btn"
            onClick={() => setCollapsed((v) => !v)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span aria-hidden="true">{collapsed ? '›' : '‹'}</span>
          </button>
          <div className="shell-user">
            <UserButton afterSignOutUrl="/" />
          </div>
        </div>
      </aside>

      <div className="shell-main">{children}</div>
    </div>
  );
}
